import OrderSource from "../types/OrderSource";

/**
 * Чистая логика «появился новый онлайн-заказ».
 *
 * Список моделей из подписки приходит целиком на каждое изменение, а «add»
 * события случаются и при пересборке окна, реконнекте, переходе заказа между
 * вкладками. Поэтому новизна определяется не событием, а тремя условиями:
 *  - id ещё не встречался (общее множество живёт всё время сессии);
 *  - источник заказа — онлайн (приложение/сайт);
 *  - заказ создан недавно (иначе это старый заказ, попавший в окно).
 * Первый батч только «запоминает» текущие заказы и не уведомляет: при входе
 * в диспетчерскую не должно звенеть на всё, что уже висит в списке.
 */

export interface TrackedOrder {
	id: number;
	source?: string | null;
	createdAt?: string | null;
}

export const ONLINE_ORDER_SOURCES: readonly string[] = [
	OrderSource.APP,
	OrderSource.WEBSITE,
];

/** Заказ старше этого возраста новым не считается (мс). */
export const ONLINE_ORDER_MAX_AGE_MS = 2 * 60_000;

/** После первого батча INIT может прийти несколькими чанками — не звенеть. */
export const ONLINE_ORDER_WARMUP_MS = 5_000;

/** Защита от бесконечного роста множества виденных id. */
const SEEN_LIMIT = 10_000;

export function isOnlineOrderSource(source: string | null | undefined) {
	return !!source && ONLINE_ORDER_SOURCES.includes(source);
}

export function createOnlineOrderTracker() {
	const seen = new Set<number>();
	let primedAt: number | null = null;

	const prune = (models: TrackedOrder[]) => {
		if (seen.size <= SEEN_LIMIT) return;
		seen.clear();
		models.forEach((model) => seen.add(model.id));
	};

	return {
		/** Возвращает id заказов, о которых нужно уведомить. */
		track(models: TrackedOrder[], now: number = Date.now()): number[] {
			const list = Array.isArray(models) ? models : [];

			if (primedAt === null) {
				primedAt = now;
				list.forEach((model) => seen.add(model.id));
				return [];
			}

			const warmingUp = now - primedAt < ONLINE_ORDER_WARMUP_MS;
			const fresh: number[] = [];

			list.forEach((model) => {
				if (model?.id === undefined || model.id === null) return;
				if (seen.has(model.id)) return;
				seen.add(model.id);

				if (warmingUp) return;
				if (!isOnlineOrderSource(model.source)) return;

				const createdAt = model.createdAt
					? new Date(model.createdAt).getTime()
					: NaN;
				if (!Number.isFinite(createdAt)) return;
				if (now - createdAt > ONLINE_ORDER_MAX_AGE_MS) return;

				fresh.push(model.id);
			});

			prune(list);

			return fresh;
		},

		has(id: number) {
			return seen.has(id);
		},

		reset() {
			seen.clear();
			primedAt = null;
		},
	};
}

export type OnlineOrderTracker = ReturnType<typeof createOnlineOrderTracker>;
